import { Button, Card, Input, Select, Space, Table } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import LmsStatusTag from '@/pages/education/components/LmsStatusTag';
import { EducationSectionSelect, EducationSubjectSelect } from '@/pages/education/components/EducationLookupSelects';
import { LMS_HOME_PATH, useLmsAccess } from '@/pages/education/utils/lmsAccess';

type Homework = {
  id: string; title: string; due_on: string; status: string; submission_count?: number;
  section_name?: string; grade_code?: string; subject_code?: string; subject_name?: string;
};

export default function HomeworkListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { canManage } = useLmsAccess();
  const q = params.get('q') || '';
  const sectionId = params.get('section_id') || undefined;
  const subjectId = params.get('subject_id') || undefined;
  const status = params.get('status') || undefined;
  const list = useQuery({
    queryKey: ['education-lms-homework', q, sectionId, subjectId, status],
    queryFn: async () => (await api.get<Homework[]>('/education/lms/homework', {
      params: { q: q || undefined, section_id: sectionId, subject_id: subjectId, status },
    })).data || [],
  });
  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    setParams(next);
  };

  return (
    <EducationPageChrome
      title={t('lmsHomework', { defaultValue: 'Homework' })}
      description={t('lmsHomeworkIntro', { defaultValue: 'Publish homework, review submissions, return feedback.' })}
      extra={<Space>
        <Button onClick={() => navigate(LMS_HOME_PATH)}>{t('lmsBackToHub', { defaultValue: 'Back to classroom' })}</Button>
        {canManage ? (
          <Button type="primary" onClick={() => navigate('/education/lms/homework/new')}>
            {t('lmsNewHomework', { defaultValue: 'New homework' })}
          </Button>
        ) : null}
      </Space>}
    >
      <Card size="small">
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            allowClear defaultValue={q} style={{ width: 260 }}
            placeholder={t('search', { defaultValue: 'Search' })}
            onSearch={(value) => setFilter('q', value.trim())}
          />
          <EducationSectionSelect allowClear style={{ width: 240 }} value={sectionId} onChange={(v) => setFilter('section_id', v)} />
          <EducationSubjectSelect allowClear style={{ width: 240 }} value={subjectId} onChange={(v) => setFilter('subject_id', v)} />
          <Select
            allowClear value={status} style={{ width: 160 }}
            placeholder={t('lmsAllStatuses', { defaultValue: 'All statuses' })}
            onChange={(v) => setFilter('status', v)}
            options={[
              { value: 'draft', label: t('lmsDraft', { defaultValue: 'Draft' }) },
              { value: 'published', label: t('lmsPublished', { defaultValue: 'Published' }) },
              { value: 'closed', label: t('lmsClosed', { defaultValue: 'Closed' }) },
            ]}
          />
        </Space>
        <Table
          rowKey="id" size="small" loading={list.isLoading} dataSource={list.data}
          pagination={{ pageSize: 25 }}
          locale={{ emptyText: t('lmsNoRows', { defaultValue: 'Nothing here yet' }) }}
          columns={[
            { title: t('lmsDueOn', { defaultValue: 'Due on' }), dataIndex: 'due_on', width: 120 },
            { title: t('title', { defaultValue: 'Title' }), dataIndex: 'title' },
            { title: t('section', { defaultValue: 'Section' }), render: (_: unknown, r: Homework) => [r.grade_code, r.section_name].filter(Boolean).join(' / ') },
            { title: t('subject', { defaultValue: 'Subject' }), render: (_: unknown, r: Homework) => r.subject_name || r.subject_code },
            { title: t('lmsSubmissions', { defaultValue: 'Submissions' }), dataIndex: 'submission_count', width: 110 },
            { title: t('status', { defaultValue: 'Status' }), dataIndex: 'status', width: 110, render: (v: string) => <LmsStatusTag status={v} /> },
            {
              title: t('actions', { defaultValue: 'Actions' }), width: 90,
              render: (_: unknown, r: Homework) => (
                <Button size="small" onClick={() => navigate(`/education/lms/homework/${r.id}`)}>{t('open', { defaultValue: 'Open' })}</Button>
              ),
            },
          ]}
        />
      </Card>
    </EducationPageChrome>
  );
}
